import { AxiosInstance } from 'axios';
import { ICartSummary, ICustomLineItem } from '../container/cart/ICart';
import { safeGet } from './../utils/objectUtils';
import { reshapeCartData } from './helper/cart/reshapeCartData';
import { setCartInfo } from '../container/seo';
import { BenefitSelectionType } from '../common/enums/common';
import { splunk_log_type, splunk_message } from '../common/splunk_constant';

interface IReqBody {
  type: string;
  data: Record<string, any> | undefined;
}

const getQuery = (type: string) => {
  switch (type) {
    case 'GET_CART': {
      return `query GetCart {
        cart {
            ...CartFields
        }
      }`;
    }
    case 'REMOVE_CART_ITEM': {
      return `mutation($removeItems: [String!]!,  $benefitId: String) {
        removeFromCart(lineItems: $removeItems, benefitId: $benefitId) {
          ...CartFields
        }
      }`;
    }
    case 'ADD_CART_ITEM': {
      return `mutation($listOfSkus: [AddFamilyToCart!]!, $benefitId: String) {
        addToCart(listOfSkus: $listOfSkus, benefitId: $benefitId) {
          ...CartFields
        }
      }`;
    }
    case 'ADJUST_QUANTITY': {
      return `mutation($adjustQuantity: [AdjustLineItemQuantity!]!, $benefitId: String) {
        adjustQuantityInCart(lineItems: $adjustQuantity, benefitId: $benefitId) {
          ...CartFields
        }
      }`;
    }
    case 'UPDATE_CART': {
      return `mutation( $benefitId: String) {
        updateCartForPricing(benefitId: $benefitId) {
          ...CartFields
        }
      }`;
    }
    case 'MOVE_CART_TO_SAVED_FOR_LATER': {
      return `mutation($listOfSkus: [AddFamilyToSaveForLater!]!,$removeItems: [String!]!) {
        moveCartToSaveForLater(listOfSkus: $listOfSkus,lineItems: $removeItems) { ...CartFields }
      }`;
    }
  }
};

const getPayload = (reqBody: IReqBody) => {
  const { data, type } = reqBody;
  const payload = JSON.stringify({
    query: `fragment LineItemFields on LineItem {
      id
      productId
      name
      quantity
      price
      totalPrice
      productType
      custom {
          fields
      }
      variant {
          id
          sku
          attributes
          assets
      }
  }

  fragment CartFields on Cart {
      customerId
      id
      version
      cartState
      lastModifiedAt
      totalPrice
      shipping
      totalLineItemQuantity
      customLineItems
      lineItems {
          ...LineItemFields
      }
  }
  ${getQuery(type)}
  `,
    variables: { ...data },
  });

  return payload;
};

export default class CartService {
  visionBffClient: AxiosInstance;
  splunkLogger: any;
  constructor(visionBffClient: AxiosInstance,splunkLogger?: any) {
    this.visionBffClient = visionBffClient;
    this.splunkLogger = splunkLogger;
  }

  private getErrorDetails(error: any) {
    const responseErrors = safeGet(error, 'response.data.errors', null);
    const responseError = Array.isArray(responseErrors) ? responseErrors[0] : responseErrors;
    const serviceError = safeGet(responseError, 'extensions.exception.response', null);

    return {
      code: safeGet(error, 'code', null),
      message: safeGet(error, 'message', null),
      status: safeGet(error, 'response.status', null),
      responseErrors,
      serviceError,
    };
  }

  private buildErrorResult(error: any, fallbackType = 'unknown_error') {
    const details = this.getErrorDetails(error);

    if (details.serviceError?.id && details.serviceError?.name) {
      return { error: details.serviceError };
    }

    return {
      error: {
        type: details.responseErrors || details.code || fallbackType,
        ...details,
      },
    };
  }

  private getConfig(type: string, data?: Record<string, any>) {
    const config = {
      method: 'POST',
      url: '/api/v1/user',
      data: getPayload({
        type,
        data,
      }),
    };
    return config;
  }

  async getCart(): Promise<any> {
    try {
      const response: any = await this.visionBffClient(this.getConfig('GET_CART'));

      if (!response) {
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: { ...splunk_message.ERROR.GET_CART_EMPTY_RESPONSE },
        });
        return { error: { type: 'empty_response' } };
      }

      if (response?.data?.errors) {
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: {
            ...splunk_message.ERROR.GET_CART_GRAPHQL_ERROR,
            responseErrors: response.data.errors,
          },
        });
      }

      if (!safeGet(response, 'data.data')) {
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: { ...splunk_message.ERROR.GET_CART_ERROR, error: safeGet(response, 'data.errors') },
        });
        return { error: safeGet(response, 'data.errors') };
      }
      const cartResponse = safeGet(response, 'data.data.cart', {});
      this.splunkLogger?.({
        logType: splunk_log_type.INFO,
        data: { ...splunk_message.SUCCESS.GET_CART_SUCCESS },
      });
      setCartInfo({
        customerId: cartResponse.customerId,
        id: cartResponse.id,
        version: String(cartResponse.version),
        totalPriceInCentAmount: String(cartResponse.totalPrice.centAmount),
        cartState: cartResponse.cartState,
      });
      const reshapedData: ICartSummary = reshapeCartData(cartResponse);
      const result = { ...reshapedData };
      return result;
    } catch (er :any) {
      const errorResult = this.buildErrorResult(er);
      const errorDetails = this.getErrorDetails(er);

      this.splunkLogger?.({
        logType: splunk_log_type.ERROR,
        data: {
          ...splunk_message.ERROR.GET_CART_ERROR,
          ...errorDetails,
          error: er,
        },
      });
      return errorResult;
    }
  }

  async updateCartForPricing(benefitId = ''): Promise<any> {
    try {
      const response: any = await this.visionBffClient(
        this.getConfig('UPDATE_CART', { benefitId }),
      );

      if (!response) {
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: {
            ...splunk_message.ERROR.UPDATE_CART_FOR_PRICING_EMPTY_RESPONSE,
            benefitId,
          },
        });
        return { error: { type: 'empty_response' } };
      }

      if (response?.data?.errors) {
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: {
            ...splunk_message.ERROR.UPDATE_CART_FOR_PRICING_GRAPHQL_ERROR,
            responseErrors: response.data.errors,
            benefitId,
          },
        });
      }

      if (benefitId && !safeGet(response, 'data.data')) {
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: {
            ...splunk_message.ERROR.UPDATE_CART_FOR_PRICING_ERROR,
            errors: safeGet(response, 'data.errors'),
            benefitId,
          },
        });
        return this.handleBenefitIdError(response);
      }

      if (!safeGet(response, 'data.data')) {
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: {
            ...splunk_message.ERROR.UPDATE_CART_FOR_PRICING_ERROR,
            error: safeGet(response, 'data.errors'),
            benefitId,
          },
        });
        return { error: safeGet(response, 'data.errors') };
      }
      const cartResponse = safeGet(response, 'data.data.updateCartForPricing', {});

      if (
        benefitId &&
        benefitId !== BenefitSelectionType.NONE &&
        benefitId !== BenefitSelectionType.DONT_USE
      ) {
        const hasBenefits =
          cartResponse.customLineItems?.some(
            (item: ICustomLineItem) => item.custom?.fields?.deductionCategory === 'benefits',
          ) || false;
        if (!hasBenefits) {
          cartResponse.isBestSavingsModalOpen = true;
        }
      }

      setCartInfo({
        customerId: cartResponse.customerId,
        id: cartResponse.id,
        version: String(cartResponse.version),
        totalPriceInCentAmount: String(cartResponse.totalPrice.centAmount),
        cartState: cartResponse.cartState,
      });

      const reshapedData: ICartSummary = reshapeCartData(cartResponse);
      const result = {
        ...reshapedData,
        isBestSavingsModalOpen: cartResponse.isBestSavingsModalOpen || false,
      };
      return result;
    } catch (er :any) {
      const errorResult = this.buildErrorResult(er);
      const errorDetails = this.getErrorDetails(er);

      this.splunkLogger?.({
        logType: splunk_log_type.ERROR,
        data: {
          ...splunk_message.ERROR.UPDATE_CART_FOR_PRICING_ERROR,
          ...errorDetails,
          error: er,
          benefitId,
        },
      });
      return errorResult;
    }
  }

  async removeCartItem(removeItems: Array<string>, benefitId = ''): Promise<any> {
    try {
      if (removeItems.length === 0) return;
      const response = await this.visionBffClient(
        this.getConfig('REMOVE_CART_ITEM', { removeItems, benefitId }),
      ).catch((res: any) => {
        // Handle errors
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: { ...splunk_message.ERROR.CART_ERROR, error: res, removeItems, benefitId },
        });
        return {
          error: {
            type: safeGet(res, 'response.data.errors', 'unknown_error'),
            res: res,
          },
        };
      });
      if (safeGet(response, 'error')) {
        return response;
      }
      if (benefitId && !safeGet(response, 'data.data')) {
        return this.handleBenefitIdError(response);
      }
      if (!safeGet(response, 'data.data')) {
        return { error: safeGet(response, 'data.errors') };
      }

      const reshapedData = reshapeCartData(safeGet(response, 'data.data.removeFromCart', {}));
      const result = { ...reshapedData };
      return result;
    } catch (er :any) {
      // Handle errors
      this.splunkLogger?.({
        logType: splunk_log_type.ERROR,
        data: { ...splunk_message.ERROR.CART_ERROR, error: er, removeItems, benefitId },
      });

      return {
        error: {
          type: 'unknown_error',
          errorInfo: er,
        },
      };
    }
  }

  async adjustCartItemQty(
    adjustQuantity: Array<Record<string, any>>,
    benefitId = '',
  ): Promise<any> {
    try {
      const response = await this.visionBffClient(
        this.getConfig('ADJUST_QUANTITY', { adjustQuantity, benefitId }),
      ).catch((res: any) => {
        // Handle errors
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: { ...splunk_message.ERROR.CART_ERROR, error: res, adjustQuantity, benefitId },
        });
        return {
          error: {
            type: safeGet(res, 'response.data.errors', 'unknown_error'),
            res: res,
          },
        };
      });
      if (safeGet(response, 'error')) {
        return response;
      }
      if (benefitId && !safeGet(response, 'data.data')) {
        return this.handleBenefitIdError(response);
      }
      if (!safeGet(response, 'data.data')) {

        return { error: safeGet(response, 'data.errors') };
      }
      const adjustQuantityInCartCartResponse = safeGet(
        response,
        'data.data.adjustQuantityInCart',
        {},
      );
      const reshapedData = reshapeCartData(adjustQuantityInCartCartResponse);
      const result = { ...reshapedData };
      return result;
    } catch (er:any) {
      // Handle errors
      this.splunkLogger?.({
        logType: splunk_log_type.ERROR,
        data: { ...splunk_message.ERROR.CART_ERROR, error: er, adjustQuantity, benefitId },
      });

      return {
        error: {
          type: 'unknown_error',
          errorInfo: er,
        },
      };
    }
  }

  async addCartItem(listOfSkus: Array<Record<string, any>>, benefitId = ''): Promise<any> {
    try {
      const response = await this.visionBffClient(
        this.getConfig('ADD_CART_ITEM', { listOfSkus, benefitId }),
      ).catch((res: any) => {
        // Handle errors
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: { ...splunk_message.ERROR.CART_ERROR, error: res, listOfSkus, benefitId },
        });
        return {
          error: {
            type: safeGet(res, 'response.data.errors', 'unknown_error'),
            res: res,
          },
        };
      });
      if (safeGet(response, 'error')) {
        return response;
      }
      if (benefitId && !safeGet(response, 'data.data')) {
        return this.handleBenefitIdError(response);
      }
      if (!safeGet(response, 'data.data')) {

        return { error: safeGet(response, 'data.errors') };
      }
      const addToCartResponse = safeGet(response, 'data.data.addToCart', {});
      const reshapedData = reshapeCartData(addToCartResponse);

      const result = { ...reshapedData };
      return result;
    } catch (er:any) {
      // Handle errors
      this.splunkLogger?.({
        logType: 'splunk_log_type.ERROR',
        data: { ...splunk_message.ERROR.CART_ERROR, error: er, listOfSkus, benefitId },
      });

      return {
        error: {
          type: 'unknown_error',
          errorInfo: er,
        },
      };
    }
  }

  async moveCartItemToSavedForLater(
    listOfSkus: Array<Record<string, any>>,
    removeItems: string[],
  ): Promise<any> {
    try {
      this.splunkLogger?.({
        logType: splunk_log_type.INFO,
        data: {
          ...splunk_message.SUCCESS.MOVE_CART_ITEM_TO_SAVED_FOR_LATER,
          listOfSkus,
          removeItems,
        },
      });
      const response = await this.visionBffClient(
        this.getConfig('MOVE_CART_TO_SAVED_FOR_LATER', { listOfSkus, removeItems }),
      ).catch((res: any) => {
        // Handle errors
        this.splunkLogger?.({
          logType: splunk_log_type.ERROR,
          data: { ...splunk_message.ERROR.CART_ERROR, error: res, listOfSkus, removeItems },
        });
        return {
          error: {
            type: safeGet(res, 'response.data.errors', 'unknown_error'),
            errorInfo: res,
          },
        };
      });
      if (safeGet(response, 'error')) {

        return response;
      }
      if (!safeGet(response, 'data.data')) {
  
        return { error: safeGet(response, 'data.errors') };
      }
      const moveCartToSaveForLaterResponse = safeGet(
        response,
        'data.data.moveCartToSaveForLater',
        {},
      );
      const reshapedData = reshapeCartData(moveCartToSaveForLaterResponse);
      const result = { ...reshapedData };
      return result;
    } catch (er :any) {
      this.splunkLogger?.({
        logType: splunk_log_type.ERROR,
        data: { ...splunk_message.ERROR.CART_ERROR, error: er, listOfSkus, removeItems },
      });
      return {
        error: {
          type: 'unknown_error',
          errorInfo: er,
        },
      };
    }
  }

  private handleBenefitIdError(response: any): any {
    const errorResponse = safeGet(response?.data?.errors[0], 'extensions.exception.response', null);

    const errorId = safeGet(errorResponse, 'id', null);
    const errorName = safeGet(errorResponse, 'name', null);

    if (errorId !== null && errorName !== null) {
      return { error: errorResponse };
    } else {
      return { error: safeGet(response, 'data.errors') };
    }
  }

  async updateCartPd(serializedPd: string): Promise<any> {
    const query = {
      query: `
        mutation ($pd: String!) {
          updateCartPd(pd: $pd)
        }
      `,
      variables: {
        pd: serializedPd,
      },
    };

    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: query,
      });
      return response?.data?.data?.updateCartPd;
    } catch (error) {
      this.splunkLogger?.({
        logType: splunk_log_type.ERROR,
        data: { ...splunk_message.ERROR.UPDATE_CART_PD_ERROR, error },
      });
      return false;
    }
  }
}
